import type { ReactNode } from 'react';
import { EmergeLockup } from '@/components/EmergeLockup';

/*
 * The brand half of the sign-out split screen.
 *
 * This carries real copy — a product statement and three proof points — so it
 * is NOT `aria-hidden`. It is a `<aside>` (complementary landmark) sitting
 * outside `<main>`, and AuthLayout declares it after the sign-in card in the
 * DOM and pulls it left with `lg:order-first`, so a screen reader reaches the
 * form first and the supporting copy second.
 */

const STROKE = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

function PartnershipsIcon() {
  return (
    <svg {...STROKE} className="h-4 w-4" aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function BillingIcon() {
  return (
    <svg {...STROKE} className="h-4 w-4" aria-hidden="true">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
      <path d="M6 15h4" />
    </svg>
  );
}

function ForecastIcon() {
  return (
    <svg {...STROKE} className="h-4 w-4" aria-hidden="true">
      <path d="M22 7 13.5 15.5 8.5 10.5 2 17" />
      <path d="M16 7h6v6" />
    </svg>
  );
}

interface ProofPoint {
  icon: ReactNode;
  title: string;
  description: string;
}

const PROOF_POINTS: readonly ProofPoint[] = [
  {
    icon: <PartnershipsIcon />,
    title: 'University partnerships',
    description: 'Institutions, contacts and agreements on one record.',
  },
  {
    icon: <BillingIcon />,
    title: 'Billing and invoicing',
    description: 'Raise, track and reconcile partner invoices.',
  },
  {
    icon: <ForecastIcon />,
    title: 'Deliverables and forecasting',
    description: 'Commitments, due dates and revenue in one view.',
  },
];

/**
 * Hidden below `lg` — the sign-in card carries everything essential on its own.
 * Colours here are fixed brand values on purpose: the navy field does not swap
 * with the colour scheme, so the text on it must not either. Headings use the
 * fixed `text-white` and supporting text the fixed `seed-cream`, rather than the
 * theme text tokens — and rather than `fg-on-accent`, which is NOT white in the
 * dark theme (it inverts to dark ink for the light dark-theme accent), so it
 * would read navy-on-navy here.
 *
 * The field is a plain navy: the grid texture and the blurred orange glow that
 * used to sit behind it were template decoration and are gone.
 */
export function BrandPanel() {
  return (
    <aside className="relative hidden overflow-hidden bg-seed-navy lg:order-first lg:flex lg:flex-col lg:justify-between lg:gap-10 lg:px-12 lg:py-10 xl:px-16">
      {/*
       * `onNavy` is required here, not decorative: this field is a FIXED
       * seed-navy in both themes, so the lockup's theme-following default ink
       * would be navy-on-navy in the light theme.
       */}
      <div className="relative">
        <EmergeLockup tone="onNavy" className="w-[228px] max-w-full" />
      </div>

      <div className="relative">
        <h2 className="max-w-[22ch] text-2xl font-semibold tracking-[-0.01em] text-balance text-white">
          The system of record for SEED university partnerships.
        </h2>
        <p className="mt-3 max-w-[44ch] text-sm text-seed-cream/75">
          One shared view of the partner portfolio, from agreement to invoice.
        </p>

        <ul className="mt-8 grid gap-5">
          {PROOF_POINTS.map((point) => (
            <li key={point.title} className="flex gap-3">
              <span className="mt-0.5 shrink-0 text-seed-cream/70">{point.icon}</span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-white">{point.title}</span>
                <span className="mt-0.5 block text-xs text-seed-cream/70">{point.description}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <p className="relative text-xs text-seed-cream/65">
        Internal system. Access is provisioned by your SEED administrator.
      </p>
    </aside>
  );
}
